import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { X, ArrowRight, Play } from "lucide-react"
import welcomeVideoUrl from "@assets/welcome-video.mp4"

interface WelcomeVideoModalProps {
  isOpen: boolean
  onClose: () => void
  autoplayUnmuted?: boolean
}

export function WelcomeVideoModal({ isOpen, onClose, autoplayUnmuted = false }: WelcomeVideoModalProps) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [ctaEnabled, setCTAEnabled] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [showSkipButton, setShowSkipButton] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showFallback, setShowFallback] = useState(false)

  useEffect(() => {
    // Show Skip button after 5 seconds
    const skipTimer = setTimeout(() => {
      setShowSkipButton(true)
    }, 5000)

    // Enable CTA after 5 seconds
    const ctaTimer = setTimeout(() => {
      setCTAEnabled(true)
    }, 5000)

    return () => {
      clearTimeout(skipTimer)
      clearTimeout(ctaTimer)
    }
  }, [])

  const handleVideoPlay = () => {
    if (videoRef.current) {
      // User clicked play — unmute on desktop (user interaction allows it)
      if (window.innerWidth >= 1024) {
        videoRef.current.muted = false
      }
      videoRef.current.play().catch(() => {
        setVideoError(true)
        setShowFallback(true)
      })
    }
  }

  const handleOnPlay = () => {
    // Called when video actually starts playing (autoplay or manual)
    setIsVideoPlaying(true)
    
    // Unmute video if triggered from sidebar (user interaction allows unmuted playback)
    if (autoplayUnmuted && videoRef.current) {
      videoRef.current.muted = false
    }
  }

  const handleVideoEnded = () => {
    setCTAEnabled(true)
    // Auto-close modal after video ends
    setTimeout(() => {
      handleClose()
    }, 1000)
  }

  const handleVideoError = () => {
    setVideoError(true)
    setShowFallback(true)
    setCTAEnabled(true)
  }

  const handleClose = () => {
    // Set localStorage flag
    localStorage.setItem('hasSeenWelcomeVideo', 'true')
    onClose()
  }

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center animate-in fade-in duration-300">
      {/* Semi-transparent black overlay */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Video Box - Responsive for portrait video */}
      <div className="relative w-[90%] max-w-[450px] md:max-w-[800px] bg-card rounded-lg shadow-2xl border border-border overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Skip Button - appears after 5 seconds */}
        {showSkipButton && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-[100] hover-elevate bg-black/50 hover:bg-black/70 text-white animate-in fade-in duration-300"
            onClick={handleClose}
            data-testid="button-skip-video"
          >
            <X className="h-5 w-5" />
          </Button>
        )}

        {/* Video Content */}
        <div className="p-4 md:p-8">
          <div className="mb-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-playfair text-foreground mb-2">
              Willkommen beim Social Media A-Team! 👋
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Erfahren Sie in 45 Sekunden, wie Lina Ihnen hilft, besseren Content zu erstellen
            </p>
          </div>

          {/* Video Player or Fallback */}
          {!showFallback ? (
            <div className="relative mb-6 rounded-lg overflow-hidden border border-border bg-black md:aspect-video">
              <video
                ref={videoRef}
                className="w-full md:h-full md:object-contain"
                controls
                autoPlay={window.innerWidth < 1024}
                muted={window.innerWidth < 1024}
                playsInline
                onPlay={handleOnPlay}
                onEnded={handleVideoEnded}
                onError={handleVideoError}
                poster="/assets/video-poster.jpg"
                data-testid="video-welcome"
              >
                <source src={welcomeVideoUrl} type="video/mp4" />
                <p className="text-center text-muted-foreground p-8">
                  Ihr Browser unterstützt leider keine Video-Wiedergabe.
                </p>
              </video>

              {/* Play Button Overlay (if autoplay fails) */}
              {!isVideoPlaying && !videoError && (
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-10"
                  onClick={handleVideoPlay}
                >
                  <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center hover-elevate transition-all">
                    <Play className="h-10 w-10 text-primary-foreground ml-1" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Fallback: Image Slideshow Placeholder
            <div className="relative bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg overflow-hidden mb-6 border border-border flex items-center justify-center md:aspect-video py-12">
              <div className="text-center p-8 space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                  <Play className="h-8 w-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-foreground">Willkommen im Team!</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Lina hilft Ihnen dabei, professionellen Content zu erstellen:
                    mit KI-gestützten Vorschlägen, automatisierten Abläufen und Ihrem persönlichen Content-Team.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CTA Button */}
          <Button
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground transition-all"
            size="lg"
            disabled={!ctaEnabled}
            onClick={handleClose}
            data-testid="button-explore-dashboard"
          >
            Cockpit entdecken
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>

          {!ctaEnabled && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              Button wird in wenigen Sekunden aktiviert...
            </p>
          )}

          {/* Dev Mode: Quick Close Button (for testing) */}
          {import.meta.env.DEV && (
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-2 text-xs"
              onClick={handleClose}
              data-testid="button-dev-close"
            >
              🔧 Dev Mode: Sofort schließen
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
